import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useApi } from "../lib/api";
import { Card, CardTitle, StatCard } from "../components/ui/Card";
import { Badge } from "../components/ui/Badge";
import { Button } from "../components/ui/Button";
import { Input } from "../components/ui/Input";
import { Select } from "../components/ui/Select";
import { DataState } from "../components/ui/DataState";
import {
  ORG_TYPES,
  type AdminOrganization,
  type AdminUserRow,
  type InviteRow,
  type Organization,
  type Role,
} from "../lib/types";

interface PilotResponse {
  metrics: {
    users: Record<string, number | null> | null;
    reports: Record<string, number | null> | null;
    organizations: Record<string, number | null> | null;
    avg_review_hours: number | null;
  } | null;
  pending_invites: InviteRow[];
  pending_access_requests: Array<{
    id: number;
    full_name: string;
    email: string;
    organization_name: string | null;
    community_role: string | null;
    city: string | null;
    neighborhood: string | null;
    status: string;
    created_at: string;
  }>;
  recently_approved_users: AdminUserRow[];
  reports_by_status: Array<{ status: string; n: number }>;
  recent_alerts: Array<{
    id: number;
    title: string;
    category: string;
    neighborhood: string | null;
    urgency: string;
    status: string;
    updated_at: string;
  }>;
  verifiers: Array<{
    id: number;
    email: string;
    full_name: string | null;
    last_seen_at: string | null;
    is_active: number;
    assigned_count: number;
  }>;
  organizations: AdminOrganization[];
}

const ROLE_OPTIONS: { value: Role; label: string }[] = [
  { value: "community_member", label: "Community member" },
  { value: "trusted_verifier", label: "Trusted verifier" },
  { value: "organization_admin", label: "Organization admin" },
  { value: "platform_admin", label: "Platform admin" },
];

export default function AdminPilot() {
  const api = useApi();
  const [data, setData] = useState<PilotResponse | null>(null);
  const [orgs, setOrgs] = useState<Organization[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [pilot, orgList] = await Promise.all([
        api.get<PilotResponse>("/api/admin/pilot"),
        api.get<{ organizations: Organization[] }>("/api/organizations"),
      ]);
      setData(pilot);
      setOrgs(orgList.organizations);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not load console");
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <div className="container-page py-12">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-3xl font-semibold text-white">Pilot console</h1>
          <p className="mt-2 max-w-3xl text-slate-300">
            Operational view of the pilot — invites, access, reports, alerts,
            organizations, and trust governance in one place.
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="secondary" onClick={() => void load()}>
            Refresh
          </Button>
        </div>
      </header>

      <DataState
        loading={loading}
        error={error}
        data={data}
        loadingMessage="Loading pilot console…"
      >
        {(d) => (
          <div className="mt-8 grid gap-8">
            <Metrics metrics={d.metrics} />

            <div className="grid gap-6 lg:grid-cols-2">
              <InvitesSection
                invites={d.pending_invites}
                orgs={orgs}
                onChanged={load}
              />
              <AccessRequestsSection
                items={d.pending_access_requests}
                onChanged={load}
              />
            </div>

            <ReportsByStatus rows={d.reports_by_status} />

            <div className="grid gap-6 lg:grid-cols-2">
              <RecentUsers items={d.recently_approved_users} />
              <VerifierActivity items={d.verifiers} />
            </div>

            <RecentAlerts items={d.recent_alerts} />

            <OrganizationsSection orgs={d.organizations} onChanged={load} />
          </div>
        )}
      </DataState>
    </div>
  );
}

function num(v: number | null | undefined) {
  return v == null ? "—" : v.toLocaleString();
}

function Metrics({ metrics }: { metrics: PilotResponse["metrics"] }) {
  if (!metrics) {
    return (
      <Card>
        <CardTitle>Pilot health</CardTitle>
        <p className="mt-2 text-sm text-slate-400">
          Metrics unavailable. Recent schema changes may need a redeploy.
        </p>
      </Card>
    );
  }
  const u = metrics.users ?? {};
  const r = metrics.reports ?? {};
  const o = metrics.organizations ?? {};
  const avg = metrics.avg_review_hours;
  return (
    <section>
      <h2 className="text-xs uppercase tracking-wider text-slate-400">
        Pilot health
      </h2>
      <div className="mt-3 grid gap-3 grid-cols-2 lg:grid-cols-4">
        <StatCard
          label="Total users"
          value={num(u.total)}
          hint={`${num(u.active)} active`}
        />
        <StatCard
          label="Active last 7d"
          value={num(u.active_last_7d)}
          hint="last_seen_at < 7 days ago"
        />
        <StatCard
          label="Pending pilot access"
          value={num(u.pending_pilot_access)}
          hint="Community members without an accepted invite"
        />
        <StatCard
          label="Avg review (hours)"
          value={avg == null ? "—" : avg.toFixed(1)}
          hint="Submitted → approved/rejected/resolved"
        />
        <StatCard
          label="Reports submitted"
          value={num(r.total)}
          hint={`${num(r.approved)} approved · ${num(r.rejected)} rejected`}
        />
        <StatCard
          label="Awaiting verification"
          value={num(
            (r.submitted ?? 0) +
              (r.needs_verification ?? 0) +
              (r.more_info_needed ?? 0),
          )}
          hint="submitted + needs_verification + more_info_needed"
        />
        <StatCard
          label="Active organizations"
          value={num(o.active)}
          hint={`${num(o.total)} total`}
        />
        <StatCard
          label="Verifiers"
          value={num(u.verifiers)}
          hint={`${num(u.org_admins)} org admins · ${num(u.platform_admins)} platform admins`}
        />
      </div>
    </section>
  );
}

function InvitesSection({
  invites,
  orgs,
  onChanged,
}: {
  invites: InviteRow[];
  orgs: Organization[];
  onChanged: () => void;
}) {
  const api = useApi();
  const [open, setOpen] = useState(true);
  const [form, setForm] = useState({
    email: "",
    role: "community_member" as Role,
    organization_id: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      await api.post("/api/admin/invites", {
        email: form.email.trim().toLowerCase(),
        role: form.role,
        organization_id: form.organization_id
          ? Number(form.organization_id)
          : null,
      });
      setForm({
        email: "",
        role: "community_member",
        organization_id: "",
      });
      onChanged();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Invite failed");
    } finally {
      setSubmitting(false);
    }
  };

  const revoke = async (id: number) => {
    try {
      await api.patch(`/api/admin/invites/${id}`, { status: "revoked" });
      onChanged();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not revoke");
    }
  };

  const copy = async (row: InviteRow) => {
    const url = `${window.location.origin}/sign-up?email=${encodeURIComponent(row.email)}`;
    try {
      await navigator.clipboard.writeText(url);
    } catch {
      // ignore — environments without clipboard permission
    }
  };

  return (
    <Card>
      <div className="flex items-center justify-between">
        <CardTitle>Pending invites</CardTitle>
        <Button size="sm" variant="ghost" onClick={() => setOpen((s) => !s)}>
          {open ? "Hide form" : "New invite"}
        </Button>
      </div>
      {open && (
        <form
          onSubmit={submit}
          className="surface-soft mt-4 grid gap-3 p-4 sm:grid-cols-3"
        >
          <Input
            required
            type="email"
            label="Email"
            value={form.email}
            onChange={(e) =>
              setForm((s) => ({ ...s, email: e.target.value }))
            }
            className="sm:col-span-3"
          />
          <Select
            label="Role"
            value={form.role}
            onChange={(e) =>
              setForm((s) => ({ ...s, role: e.target.value as Role }))
            }
          >
            {ROLE_OPTIONS.map((r) => (
              <option key={r.value} value={r.value}>
                {r.label}
              </option>
            ))}
          </Select>
          <Select
            label="Organization"
            value={form.organization_id}
            onChange={(e) =>
              setForm((s) => ({ ...s, organization_id: e.target.value }))
            }
            className="sm:col-span-2"
          >
            <option value="">— None —</option>
            {orgs.map((o) => (
              <option key={o.id} value={o.id}>
                {o.name}
              </option>
            ))}
          </Select>
          {error && (
            <p className="rounded-md border border-red-400/30 bg-red-500/10 px-3 py-2 text-xs text-red-200 sm:col-span-3">
              {error}
            </p>
          )}
          <div className="flex justify-end sm:col-span-3">
            <Button type="submit" size="sm" disabled={submitting}>
              {submitting ? "Issuing…" : "Issue invite"}
            </Button>
          </div>
        </form>
      )}
      <ul className="mt-4 divide-y divide-white/5">
        {invites.length === 0 && (
          <li className="py-4 text-sm text-slate-400">
            No pending invites. Issue one above.
          </li>
        )}
        {invites.map((i) => (
          <li
            key={i.id}
            className="flex flex-wrap items-center justify-between gap-3 py-3"
          >
            <div className="min-w-0">
              <div className="truncate text-sm font-semibold text-white">
                {i.email}
              </div>
              <div className="text-xs text-slate-400">
                {i.role.replace("_", " ")}
                {i.organization_name && ` · ${i.organization_name}`}
                {" · "}
                {i.created_at}
              </div>
            </div>
            <div className="flex gap-2">
              <Button size="sm" variant="ghost" onClick={() => void copy(i)}>
                Copy link
              </Button>
              <Button
                size="sm"
                variant="danger"
                onClick={() => void revoke(i.id)}
              >
                Revoke
              </Button>
            </div>
          </li>
        ))}
      </ul>
    </Card>
  );
}

function AccessRequestsSection({
  items,
  onChanged,
}: {
  items: PilotResponse["pending_access_requests"];
  onChanged: () => void;
}) {
  const api = useApi();
  const update = async (id: number, status: "approved" | "rejected") => {
    try {
      await api.patch(`/api/admin/access-requests/${id}/status`, { status });
      onChanged();
    } catch {
      // surfaced on the AdminReview page; here it's best-effort.
    }
  };
  return (
    <Card>
      <CardTitle>Pending access requests</CardTitle>
      {items.length === 0 ? (
        <p className="mt-3 text-sm text-slate-400">
          Inbox clear. New requests will arrive here from /request-access.
        </p>
      ) : (
        <ul className="mt-4 divide-y divide-white/5">
          {items.map((r) => (
            <li key={r.id} className="grid gap-1 py-3">
              <div className="flex items-center justify-between gap-2">
                <span className="text-sm font-semibold text-white">
                  {r.full_name}
                </span>
                <span className="text-xs text-slate-500">{r.created_at}</span>
              </div>
              <p className="break-all text-xs text-slate-400">{r.email}</p>
              {(r.organization_name || r.community_role) && (
                <p className="text-xs text-slate-400">
                  {r.community_role && (
                    <span className="text-slate-300">
                      {r.community_role}
                    </span>
                  )}
                  {r.organization_name && r.community_role && " · "}
                  {r.organization_name}
                </p>
              )}
              <div className="mt-1 flex gap-2">
                <Button
                  size="sm"
                  onClick={() => void update(r.id, "approved")}
                >
                  Approve
                </Button>
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={() => void update(r.id, "rejected")}
                >
                  Reject
                </Button>
              </div>
            </li>
          ))}
        </ul>
      )}
      <p className="mt-3 text-xs text-slate-500">
        Approving a request doesn't auto-create an invite. Issue one with the
        approved email in the invite form.
      </p>
    </Card>
  );
}

function ReportsByStatus({
  rows,
}: {
  rows: Array<{ status: string; n: number }>;
}) {
  if (rows.length === 0) {
    return (
      <Card>
        <CardTitle>Reports by status</CardTitle>
        <p className="mt-2 text-sm text-slate-400">
          No reports submitted yet.
        </p>
      </Card>
    );
  }
  const labels: Record<string, string> = {
    submitted: "Pending",
    needs_verification: "Under review",
    more_info_needed: "More info needed",
    approved: "Approved",
    rejected: "Rejected",
    resolved: "Resolved",
  };
  return (
    <Card>
      <CardTitle>Reports by status</CardTitle>
      <ul className="mt-4 grid gap-2 sm:grid-cols-3 lg:grid-cols-6">
        {rows.map((r) => (
          <li key={r.status} className="surface-soft px-3 py-2">
            <div className="text-[11px] uppercase tracking-wider text-slate-400">
              {labels[r.status] ?? r.status}
            </div>
            <div className="mt-1 text-xl font-semibold text-white">{r.n}</div>
          </li>
        ))}
      </ul>
    </Card>
  );
}

function RecentUsers({ items }: { items: AdminUserRow[] }) {
  return (
    <Card>
      <div className="flex items-center justify-between">
        <CardTitle>Recently approved / elevated users</CardTitle>
        <Link to="/admin/users" className="text-xs text-accent hover:underline">
          Manage all
        </Link>
      </div>
      {items.length === 0 ? (
        <p className="mt-3 text-sm text-slate-400">
          Nothing yet. Once invites are accepted, users appear here.
        </p>
      ) : (
        <ul className="mt-4 divide-y divide-white/5">
          {items.map((u) => (
            <li
              key={u.id}
              className="flex items-center justify-between gap-3 py-3"
            >
              <div className="min-w-0">
                <div className="truncate text-sm font-semibold text-white">
                  {u.full_name ?? u.email}
                </div>
                <div className="text-xs text-slate-400">
                  {u.role.replace("_", " ")}
                  {u.organization_name && ` · ${u.organization_name}`}
                  {u.last_seen_at && ` · last seen ${u.last_seen_at}`}
                </div>
              </div>
              <Badge tone={u.is_active ? "success" : "critical"}>
                {u.is_active ? "active" : "suspended"}
              </Badge>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}

function VerifierActivity({
  items,
}: {
  items: PilotResponse["verifiers"];
}) {
  return (
    <Card>
      <CardTitle>Verifier activity</CardTitle>
      {items.length === 0 ? (
        <p className="mt-3 text-sm text-slate-400">
          No trusted verifiers yet. Promote one from the user management page.
        </p>
      ) : (
        <ul className="mt-4 divide-y divide-white/5">
          {items.map((v) => (
            <li
              key={v.id}
              className="flex items-center justify-between gap-3 py-3"
            >
              <div className="min-w-0">
                <div className="truncate text-sm font-semibold text-white">
                  {v.full_name ?? v.email}
                </div>
                <div className="text-xs text-slate-400">
                  {v.last_seen_at ? `Last seen ${v.last_seen_at}` : "Never seen"}
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Badge>{v.assigned_count} assigned</Badge>
                <Badge tone={v.is_active ? "success" : "critical"}>
                  {v.is_active ? "active" : "suspended"}
                </Badge>
              </div>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}

function RecentAlerts({
  items,
}: {
  items: PilotResponse["recent_alerts"];
}) {
  return (
    <Card>
      <div className="flex items-center justify-between">
        <CardTitle>Recent alerts</CardTitle>
        <Link to="/alerts" className="text-xs text-accent hover:underline">
          View community feed
        </Link>
      </div>
      {items.length === 0 ? (
        <p className="mt-3 text-sm text-slate-400">
          No active alerts. Approve a report from /admin to publish the first.
        </p>
      ) : (
        <ul className="mt-4 divide-y divide-white/5">
          {items.map((a) => (
            <li key={a.id} className="py-3">
              <div className="flex flex-wrap items-center gap-2">
                <Badge tone="success">{a.status}</Badge>
                <Badge>{a.urgency}</Badge>
                {a.neighborhood && <Badge>{a.neighborhood}</Badge>}
                <Badge>{a.category.replace(/_/g, " ")}</Badge>
              </div>
              <div className="mt-2 text-sm font-semibold text-white">
                {a.title}
              </div>
              <div className="text-xs text-slate-400">
                Updated {a.updated_at}
              </div>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}

function OrganizationsSection({
  orgs,
  onChanged,
}: {
  orgs: AdminOrganization[];
  onChanged: () => void;
}) {
  const api = useApi();
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ name: "", type: "business" });
  const [error, setError] = useState<string | null>(null);

  const create = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    try {
      await api.post("/api/organizations", form);
      setForm({ name: "", type: "business" });
      setCreating(false);
      onChanged();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not create");
    }
  };

  const setArchived = async (id: number, archived: boolean) => {
    try {
      await api.patch(`/api/admin/organizations/${id}`, {
        is_active: !archived,
      });
      onChanged();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not update");
    }
  };

  return (
    <Card>
      <div className="flex items-center justify-between">
        <CardTitle>Organizations</CardTitle>
        <Button
          size="sm"
          variant="ghost"
          onClick={() => setCreating((s) => !s)}
        >
          {creating ? "Cancel" : "Add organization"}
        </Button>
      </div>
      {creating && (
        <form onSubmit={create} className="surface-soft mt-4 grid gap-3 p-4 sm:grid-cols-3">
          <Input
            required
            label="Name"
            value={form.name}
            onChange={(e) => setForm((s) => ({ ...s, name: e.target.value }))}
            className="sm:col-span-2"
          />
          <Select
            label="Type"
            value={form.type}
            onChange={(e) => setForm((s) => ({ ...s, type: e.target.value }))}
          >
            {ORG_TYPES.map((t) => (
              <option key={t.value} value={t.value}>
                {t.label}
              </option>
            ))}
          </Select>
          {error && (
            <p className="rounded-md border border-red-400/30 bg-red-500/10 px-3 py-2 text-xs text-red-200 sm:col-span-3">
              {error}
            </p>
          )}
          <div className="flex justify-end sm:col-span-3">
            <Button type="submit" size="sm">
              Create
            </Button>
          </div>
        </form>
      )}
      {orgs.length === 0 ? (
        <p className="mt-3 text-sm text-slate-400">
          No organizations yet. Add the pilot's anchor organizations first.
        </p>
      ) : (
        <ul className="mt-4 divide-y divide-white/5">
          {orgs.map((o) => {
            const archived = !o.is_active;
            return (
              <li
                key={o.id}
                className="flex flex-wrap items-center justify-between gap-3 py-3"
              >
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <span
                      className={`truncate text-sm font-semibold ${archived ? "text-slate-400" : "text-white"}`}
                    >
                      {o.name}
                    </span>
                    <Badge>{o.type.replace("_", " ")}</Badge>
                    {archived && <Badge tone="critical">archived</Badge>}
                  </div>
                  <div className="mt-1 text-xs text-slate-400">
                    {o.member_count} members · {o.verifier_count} verifiers ·{" "}
                    {o.report_count} reports
                    {o.last_report_at && ` · last ${o.last_report_at}`}
                  </div>
                </div>
                <Button
                  size="sm"
                  variant={archived ? "secondary" : "danger"}
                  onClick={() => void setArchived(o.id, !archived)}
                >
                  {archived ? "Reactivate" : "Archive"}
                </Button>
              </li>
            );
          })}
        </ul>
      )}
    </Card>
  );
}
